import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { gaps } from "../../../primitives/spacing";
import { SectionTitle } from "../../../primitives/data-display/SectionTitle";
import { Subtitle } from "../../../primitives/data-display/Subtitle";
import { Note } from "../../../primitives/data-display/Note";
import { CenteredStack } from "../../../primitives/layout/CenteredStack";
import type { ErrorCode } from "../../../../lib/errors";
import { useErrorState } from "./useErrorState";

interface ErrorStateProps {
  code: ErrorCode;
  /** Overrides the registry's default description, e.g. to interpolate error `details`. */
  description?: ReactNode;
  /** Optional retry/back action slot (e.g. a "Try again" button). */
  action?: ReactNode;
  minHeight?: number | string;
}

/** Large centered icon + title + description + a small error-code caption, for failures that
 *  deserve more than an inline alert (an LLM outage, a rate limit, an unreadable document). */
export function ErrorState({ code, description, action, minHeight = 320 }: ErrorStateProps) {
  const { t } = useTranslation();
  const copy = useErrorState(code);

  return (
    <CenteredStack gap={gaps.base} $minHeight={minHeight} $textAlign="center">
      {copy.icon}
      <SectionTitle>{copy.title}</SectionTitle>
      <Subtitle>{description ?? copy.description}</Subtitle>
      {action}
      <Note color="text.disabled">{t("errors.codeLabel", { code })}</Note>
    </CenteredStack>
  );
}
